import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Cell,
    HeaderGroup,
    Row,
    useBlockLayout,
    useGlobalFilter,
    useSortBy,
    useTable,
} from 'react-table';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import * as naturalCompare from 'string-natural-compare';

import {
    Checkbox,
    CenteredHeader,
    GlobalFilter,
    TableHeader,
    TableHeaderGroup,
    TableFooterGroup,
    CompactNumberCell,
} from '@trqk/components';
import {
    createFuzzyTextFilter,
    formatDollarsCompact,
    getIncludedGapChannelIds,
    getScrollbarWidth,
    locale,
} from '@trqk/utils';

import {
    rowHeight,
    defaultColumnWidth,
    tableHeight,
    COLUMN_IDS,
    columnWidths,
} from '../constants';
import {
    ChannelNameCell,
    DetectionsCell,
    EstimatedLossCell,
    GapCell,
    IncludeChannelCell,
    PaymentsCell,
    PerformancesCell,
} from './cell';
import {
    ChannelFooter,
    EstimatedLossFooter,
    IncludeChannelFooter,
} from './footer';
import {
    getGapChannelDetectionsCellValue,
    getGapChannelEstimatedLossCellValue,
    getGapChannelGapCellValue,
    getGapChannelIncludeChannelCellValue,
    getGapChannelPerformancesCellValue,
    getIncludedFooter,
} from '../utils';

interface ConfirmChannelsTableProps {
    context: GapAnalysisContext;
    /**
     * When this number changes, the table data will be reset to the current value of `gapChannels`.
     */
    refreshTime: number;
    /**
     * Table data to be displayed
     */
    gapChannels: TrqkGapChannel[];
    /**
     * Calculated stats about the table data, used in rendering various cells
     */
    gapChannelStats: GapChannelStats;
    send: SendGapAnalysisEvent;
}

const {
    enUS: {
        MANAGE_GAP_ANALYSIS: {
            STEPS: {
                CONFIRM_CHANNELS: { COLUMN_HEADERS },
            },
        },
    },
} = locale;

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders a table of channels and associated details during creation of a Gap Analysis report. The
 * table allows the user to select individual channels to include in the report. For each channel,
 * the user can select individual works (in a separate step) to include them in the report.
 */
export const ConfirmChannelsTable = ({
    context,
    refreshTime,
    gapChannels,
    gapChannelStats,
    send,
}: ConfirmChannelsTableProps): JSX.Element => {
    const history = useHistory();
    const [data, setData] = useState(gapChannels);
    const { channels, edits, statementId } = context;
    const editsString = JSON.stringify(edits);
    const includedGapChannelIds = getIncludedGapChannelIds(channels);
    const { detectionsMax, performancesMax } = gapChannelStats;
    const hasCheckedChannel = Object.values(edits).some(
        ({ checked }) => checked,
    );
    const allChannelsAreChecked = Object.values(edits).every(
        ({ checked }) => checked,
    );
    const someChannelsAreChecked = !allChannelsAreChecked && hasCheckedChannel;

    // When our cell renderer calls updateData, use the item id, columnId and new value to update
    // the original data
    const skipPageResetRef = useRef(false);

    const updateData = useCallback(
        (nextData: TrqkGapChannel[]) => {
            skipPageResetRef.current = true;

            setData(nextData);
        },
        [skipPageResetRef],
    );

    useEffect(() => {
        if (refreshTime !== null) {
            skipPageResetRef.current = true;

            setData(gapChannels);
        }
    }, [refreshTime]);

    useEffect(() => {
        skipPageResetRef.current = false;
    });

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const {
        location: { search },
    } = history;

    // This ensures that the bars shown in the DetectionsCell and PerformancesCell are scaled to
    // the same max value horizontally
    const gapDenominator = Math.max(detectionsMax, performancesMax, 1);

    const columns = useMemo(
        () => [
            {
                id: COLUMN_IDS.CHANNEL,
                accessor: ({
                    channel_id,
                    channel_name_detection,
                    channel_name_statement,
                }: TrqkGapChannel): GapChannelNameCellValue => ({
                    channelNameDetection: channel_name_detection,
                    channelNameStatement: channel_name_statement ?? null,
                    channelId: channel_id,
                }),
                sortType: (
                    rowA: Row<TrqkGapChannel>,
                    rowB: Row<TrqkGapChannel>,
                ): number =>
                    naturalCompare(
                        rowA.original.channel_name_detection,
                        rowB.original.channel_name_detection,
                    ),
                Cell: ChannelNameCell,
                headerLabel: COLUMN_HEADERS.CHANNEL,
                Header: TableHeader,
                Footer: ChannelFooter,
                send,
                width: columnWidths[COLUMN_IDS.CHANNEL],
            },
            {
                id: COLUMN_IDS.PROGRAMS,
                accessor: ({ programs_detected }: TrqkGapChannel) =>
                    programs_detected ?? 0,
                Cell: CompactNumberCell,
                headerLabel: COLUMN_HEADERS.PROGRAMS,
                Header: TableHeader,
                Footer: getIncludedFooter({
                    getCount: row => row.original?.programs_detected ?? 0,
                }),
                send,
                width: columnWidths[COLUMN_IDS.PROGRAMS],
            },
            {
                id: COLUMN_IDS.ADS_AND_PROMOS,
                accessor: ({ ads_promos_count_total }: TrqkGapChannel) =>
                    ads_promos_count_total ?? 0,
                Cell: CompactNumberCell,
                headerLabel: COLUMN_HEADERS.ADS_AND_PROMOS,
                Header: TableHeader,
                Footer: getIncludedFooter({
                    getCount: row => row.original?.ads_promos_count_total ?? 0,
                }),
                send,
                width: columnWidths[COLUMN_IDS.ADS_AND_PROMOS],
            },
            {
                id: COLUMN_IDS.AUDIOS,
                accessor: ({ audios_detected_raw }: TrqkGapChannel) =>
                    audios_detected_raw ?? 0,
                Cell: CompactNumberCell,
                headerLabel: COLUMN_HEADERS.AUDIOS,
                Header: TableHeader,
                Footer: getIncludedFooter({
                    getCount: row => row.original?.audios_detected_raw ?? 0,
                }),
                send,
                width: columnWidths[COLUMN_IDS.AUDIOS],
            },
            {
                id: COLUMN_IDS.DETECTIONS,
                accessor: (
                    gapChannel: TrqkGapChannel,
                ): GapChannelDetectionsCellValue =>
                    getGapChannelDetectionsCellValue({
                        gapChannel,
                        gapDenominator,
                    }),
                sortType: (
                    rowA: Row<TrqkGapChannel>,
                    rowB: Row<TrqkGapChannel>,
                ): number => {
                    if (
                        rowA.original.detections_raw >
                        rowB.original.detections_raw
                    ) {
                        return 1;
                    }

                    if (
                        rowA.original.detections_raw <
                        rowB.original.detections_raw
                    ) {
                        return -1;
                    }

                    return 0;
                },
                sortDescFirst: true,
                Cell: DetectionsCell,
                headerLabel: COLUMN_HEADERS.DETECTIONS,
                headerSubLabel: COLUMN_HEADERS.DETECTIONS_SUB_LABEL,
                Header: CenteredHeader,
                Footer: getIncludedFooter({
                    getCount: row => row.original?.detections_raw ?? 0,
                    wrapperClassNames: 'ml-5 flex items-center',
                }),
                width: columnWidths[COLUMN_IDS.DETECTIONS],
            },
            {
                id: COLUMN_IDS.GAP,
                accessor: (gapChannel: TrqkGapChannel): number =>
                    getGapChannelGapCellValue(gapChannel),
                sortDescFirst: true,
                Cell: GapCell,
                headerLabel: COLUMN_HEADERS.GAP,
                Header: CenteredHeader,
                Footer: getIncludedFooter({
                    getCount: row => getGapChannelGapCellValue(row.original),
                    wrapperClassNames: 'flex justify-center items-center pl-1',
                }),
                width: columnWidths[COLUMN_IDS.GAP],
            },
            {
                id: COLUMN_IDS.PERFORMANCES,
                accessor: (
                    gapChannel: TrqkGapChannel,
                ): GapChannelPerformancesCellValue =>
                    getGapChannelPerformancesCellValue({
                        gapChannel,
                        gapDenominator,
                        statementId,
                    }),
                sortType: (
                    rowA: Row<TrqkGapChannel>,
                    rowB: Row<TrqkGapChannel>,
                ): number => {
                    if (
                        (rowA.original.performances_matched_total ?? 0) >
                        (rowB.original.performances_matched_total ?? 0)
                    ) {
                        return 1;
                    }

                    if (
                        (rowA.original.performances_matched_total ?? 0) <
                        (rowB.original.performances_matched_total ?? 0)
                    ) {
                        return -1;
                    }

                    return 0;
                },
                sortDescFirst: true,
                Cell: PerformancesCell,
                headerLabel: COLUMN_HEADERS.PERFORMANCES,
                headerSubLabel: COLUMN_HEADERS.PERFORMANCES_SUB_LABEL,
                Header: CenteredHeader,
                Footer: getIncludedFooter({
                    getCount: row =>
                        row.original?.performances_matched_total ?? 0,
                    wrapperClassNames: 'flex items-center justify-end pr-4',
                }),
                width: columnWidths[COLUMN_IDS.PERFORMANCES],
            },
            {
                id: COLUMN_IDS.PAYMENTS,
                accessor: (gapChannel: TrqkGapChannel) =>
                    gapChannel?.payments_matched_total ?? 0,
                Cell: PaymentsCell,
                headerLabel: COLUMN_HEADERS.PAYMENTS,
                Header: TableHeader,
                Footer: getIncludedFooter({
                    getCount: row => row.original?.payments_matched_total ?? 0,
                    formatter: formatDollarsCompact,
                }),
                send,
                width: columnWidths[COLUMN_IDS.PAYMENTS],
            },
            {
                id: COLUMN_IDS.ESTIMATED_LOSS,
                accessor: (
                    gapChannel: TrqkGapChannel,
                ): GapChannelEstimatedLossCellValue =>
                    getGapChannelEstimatedLossCellValue(gapChannel),
                sortType: (
                    rowA: Row<TrqkGapChannel>,
                    rowB: Row<TrqkGapChannel>,
                ): number => {
                    const estimatedLossMaxA =
                        rowA.original.estimated_loss_max ?? 0;
                    const estimatedLossMaxB =
                        rowB.original.estimated_loss_max ?? 0;

                    if (estimatedLossMaxA > estimatedLossMaxB) {
                        return 1;
                    }

                    if (estimatedLossMaxA < estimatedLossMaxB) {
                        return -1;
                    }

                    return 0;
                },
                sortDescFirst: true,
                Cell: EstimatedLossCell,
                headerLabel: COLUMN_HEADERS.ESTIMATED_LOSS,
                Header: TableHeader,
                Footer: EstimatedLossFooter,
                width: columnWidths[COLUMN_IDS.ESTIMATED_LOSS],
            },
            {
                id: COLUMN_IDS.INCLUDE_CHANNEL,
                accessor: (
                    gapChannel: TrqkGapChannel,
                ): GapChannelIncludeCellValue =>
                    getGapChannelIncludeChannelCellValue({
                        editsString,
                        gapChannel,
                    }),
                sortType: (
                    rowA: Row<TrqkGapChannel>,
                    rowB: Row<TrqkGapChannel>,
                ): number => {
                    let editsFromString: Record<string, GapAnalysisEdits> = {};

                    try {
                        editsFromString = JSON.parse(editsString);
                    } catch {
                        return 0;
                    }

                    const rowAChecked =
                        editsFromString?.[rowA.original.channel_id]?.checked ??
                        false;
                    const rowBChecked =
                        editsFromString?.[rowB.original.channel_id]?.checked ??
                        false;

                    if (rowAChecked && !rowBChecked) {
                        return 1;
                    }

                    if (rowBChecked && !rowAChecked) {
                        return -1;
                    }

                    const checkedIsTie =
                        (rowAChecked && rowBChecked) ||
                        (!rowAChecked && !rowBChecked);

                    const rowAChannelName =
                        rowA.original.channel_name_detection ?? null;
                    const rowBChannelName =
                        rowB.original.channel_name_detection ?? null;

                    if (
                        checkedIsTie &&
                        rowAChannelName !== null &&
                        rowBChannelName !== null
                    ) {
                        return naturalCompare(rowBChannelName, rowAChannelName);
                    }

                    return 0;
                },
                sortDescFirst: true,
                Cell: IncludeChannelCell,
                headerLabel: COLUMN_HEADERS.INCLUDE_CHANNEL,
                Header: TableHeader,
                Footer: IncludeChannelFooter,
                send,
                width: columnWidths[COLUMN_IDS.INCLUDE_CHANNEL],
            },
        ],
        [
            updateData,
            data,
            search,
            send,
            gapDenominator,
            editsString,
            statementId,
        ],
    );

    const filterTypes = useMemo(
        () => ({
            globalFilter: createFuzzyTextFilter<TrqkGapChannel>({
                keys: [`values.${COLUMN_IDS.CHANNEL}.channelNameDetection`],
            }),
        }),
        [],
    );

    const {
        footerGroups,
        getTableBodyProps,
        getTableProps,
        headerGroups,
        prepareRow,
        rows,
        setGlobalFilter,
        state,
        totalColumnsWidth,
    } = useTable<TrqkGapChannel>(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            globalFilter: filterTypes.globalFilter,
            initialState: {
                sortBy: [
                    {
                        id: COLUMN_IDS.GAP,
                        desc: true,
                    },
                ],
            },
            autoResetPage: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            autoResetGroupBy: !skipPageResetRef.current,
            autoResetSelectedRows: !skipPageResetRef.current,
            // For some reason, !skipPageResetRef doesn't seem to work here, so I'm setting
            // `autoResetSortBy` to always be `false`.
            autoResetSortBy: false,
            autoResetFilters: !skipPageResetRef.current,
            autoResetGlobalFilter: false,
            autoResetRowState: !skipPageResetRef.current,
        },
        useBlockLayout,
        useGlobalFilter,
        useSortBy,
        // Selection state is managed in the gapAnalysisMachine, so we are not using
        // the useRowSelection hook.
    );

    const renderedRows = useMemo(
        () =>
            rows.map((row, index) => {
                const ariaRowIndex = index + 1;
                const evenRow = ariaRowIndex % 2 === 0;

                prepareRow(row);

                const rowProps = row.getRowProps();

                const { key: rowKey, role, style } = rowProps;

                const classNames = cx('tr relative flex items-center', {
                    // This table looks weird with only one row if it has a white background, so
                    // I'm flipping the logic here.
                    'even-row': !evenRow,
                });

                return (
                    <div
                        key={rowKey}
                        role={role}
                        aria-rowindex={ariaRowIndex}
                        className={classNames}
                        style={{
                            ...style,
                            height: rowHeight,
                        }}
                    >
                        {row.cells.map((cell: Cell<TrqkGapChannel>) => {
                            const { key, ...rest } = cell.getCellProps();

                            return (
                                <div key={key} {...rest} className="td text-sm">
                                    {cell.render('Cell')}
                                </div>
                            );
                        })}
                    </div>
                );
            }),
        [prepareRow, rows],
    );

    const scrollBarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <>
            {/*
                It feels a bit hacky, but this absolute positioning lets us keep the <GlobalFilter> within
                this component while conserving on vertical space. The `maxWidth` props set on the
                <LabeledValue>s in <DataSources> will prevent an overlap. Also, there is a width
                in the <Breadcrumbs> component that will also keep the breadcrumbs from overlapping
                the <GlobalFilter>.
            */}
            <div className="absolute">
                <div
                    className="relative"
                    style={{ top: -76, left: 590, width: 300 }}
                >
                    <GlobalFilter
                        globalFilter={state.globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        placeholder={
                            locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                .CONFIRM_CHANNELS.GLOBAL_FILTER_PLACEHOLDER
                        }
                    />
                    <div className="pt-1.5" style={{ paddingLeft: 190 }}>
                        <Checkbox
                            inputId="selectAll"
                            checked={allChannelsAreChecked}
                            indeterminate={someChannelsAreChecked}
                            label={
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .CONFIRM_WORKS.SELECT_ALL
                            }
                            labelClasses="text-sm"
                            size="large"
                            color="primary"
                            onChange={e => {
                                const target = e.target as HTMLInputElement;

                                send({
                                    type: 'TOGGLE_ALL_GAP_CHANNELS_CHECKED',
                                    value: {
                                        checked: target.checked,
                                    },
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
            {/*
                z-10 is necessary to keep mouse functionality because the headers will otherwise be
                stacked underneath the <div>s that wrap GlobalFilter above
             */}
            <div {...getTableProps()} className="trqk-table font-normal z-10">
                <div>
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<TrqkGapChannel>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<TrqkGapChannel>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>
                <div className="overflow-y-auto overflow-x-hidden respond-to-mouse">
                    <div
                        {...getTableBodyProps()}
                        style={{
                            height: tableHeight,
                            width: totalColumnsWidth + scrollBarWidth,
                        }}
                    >
                        {renderedRows}
                    </div>
                </div>
                <div
                    className={cx(
                        'font-semibold text-sm border border-tertiary-400',
                        {
                            'text-highlight-magenta':
                                includedGapChannelIds.length === 0 &&
                                !hasCheckedChannel,
                        },
                    )}
                >
                    {footerGroups.map(
                        (
                            footerGroup: HeaderGroup<TrqkGapChannel>,
                        ): JSX.Element => {
                            const { key: footerGroupKey } =
                                footerGroup.getFooterGroupProps();

                            return (
                                <TableFooterGroup<TrqkGapChannel>
                                    key={footerGroupKey}
                                    footerGroup={footerGroup}
                                />
                            );
                        },
                    )}
                </div>
            </div>
        </>
    );
};
